import PaidIcon from '@mui/icons-material/Paid';
import { EditInDialogButtonProps } from '@react-admin/ra-form-layout';
import { useCallback } from 'react';
import { useNotify, useRefresh } from 'react-admin';

import { EditInDialogButton, StepperForm } from '@sh4res/bo-common/components';
import { ConfirmationStep } from '@sh4res/bo-common/components/B2bSavingsFundRequests/B2bSavingsFundRequestsPayment/steps/ConfirmationStep';
import { PaymentStep } from '@sh4res/bo-common/components/B2bSavingsFundRequests/B2bSavingsFundRequestsPayment/steps/PaymentStep';
import { getErrorMessage, Resources } from '@sh4res/bo-common/utils';

export const B2bSavingsFundRequestsPaymentCreate = ({
  resource,
  payment_amount,
  fundRequestId,
  ...props
}: EditInDialogButtonProps['ButtonProps'] & {
  resource: Resources;
  payment_amount: number;
  fundRequestId: number;
}) => {
  const refresh = useRefresh();
  const notify = useNotify();

  const handleSuccess = useCallback(() => {
    notify('resources.bo_common.b2b.savings_funds_requests.payments.success', {
      type: 'success',
    });
    refresh();
  }, [notify, refresh]);

  const handleError = useCallback(
    (error: unknown) => {
      notify('resources.bo_common.b2b.savings_funds_requests.payments.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    },
    [notify],
  );

  return (
    <EditInDialogButton
      resource={resource}
      id={fundRequestId}
      fullWidth
      maxWidth="lg"
      title="resources.bo_common.b2b.savings_funds_requests.payments.title"
      testId="BoExternalB2bSavingsFundRequestsPaymentCreate_EditButton"
      ButtonProps={{
        variant: 'contained',
        size: 'medium',
        sx: {
          height: '30px',
          fontWeight: 'bold',
        },
        startIcon: <PaidIcon />,
        label: 'resources.bo_common.b2b.savings_funds_requests.payments.button',
        ...props,
      }}
      mutationMode="pessimistic"
      transform={() => ({ action: 'paid' })}
      mutationOptions={{
        onSuccess: handleSuccess,
        onError: handleError,
      }}
      disableEscapeKeyDown
      sx={{
        '& .MuiDialog-paper': {
          height: '100%',
        },
        '& form': {
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
        },
      }}>
      <StepperForm
        steps={[
          {
            label: 'resources.bo_common.b2b.savings_funds_requests.payments.step_payment',
            content: <PaymentStep />,
          },
          {
            label: 'resources.bo_common.b2b.savings_funds_requests.payments.step_confirmation',
            content: <ConfirmationStep payment_amount={payment_amount} />,
          },
        ]}
        sx={{
          flexGrow: 1,
          '& > div': {
            height: '100%',
          },
        }}
        permissions={{
          resource,
          action: 'edit',
        }}
        testId="BoExternalB2bSavingsFundRequestsPaymentCreate"
      />
    </EditInDialogButton>
  );
};
